import { normalizeCountryCode } from './utils/skuTitleTranslationUtils';
import dayjs from 'dayjs';

export function hashProductId(title) {
  if (!title) return '';
  let h = 5381;
  const str = String(title);
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
}

/** Normalize any date-like value to SQL date `YYYY-MM-DD`, or '' if unknown. */
export function toSqlDateKey(value) {
  if (value == null || value === '') return '';
  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value.format('YYYY-MM-DD') : '';
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : dayjs(value).format('YYYY-MM-DD');
  }
  const raw = String(value).trim();
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const dotted = raw.match(/^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})/);
  if (dotted) {
    return `${dotted[3]}-${dotted[2].padStart(2, '0')}-${dotted[1].padStart(2, '0')}`;
  }
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
}

/** Lowercase country slug for product-detail URLs, e.g. CZ → cz. */
export function productDetailCountrySlug(country) {
  const code = normalizeCountryCode(country);
  if (!code) return '';
  if (code.length === 2) return code.toLowerCase();
  return String(code)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function countrySlugMatches(itemCountry, slug) {
  if (!slug) return true;
  return productDetailCountrySlug(itemCountry) === String(slug).trim().toLowerCase();
}

export function productRowDateKey(item) {
  const raw = item?.updated_at || item?.date;
  if (!raw) return null;
  return toSqlDateKey(raw) || null;
}

function normalizeDateLabelKey(dateLabel) {
  return toSqlDateKey(dateLabel) || null;
}

/** All scrape rows on the latest date on or before dateLabel (YYYY-MM-DD). If no date, latest date. */
export function rowsOnLatestDateOnOrBefore(items, dateLabel) {
  if (!items?.length) return [];
  const dateKey = normalizeDateLabelKey(dateLabel);
  let bestKey = null;
  const keyed = items.map((item) => ({ item, key: productRowDateKey(item) }));
  for (const row of keyed) {
    if (!row.key) continue;
    if (dateKey && row.key > dateKey) continue;
    if (!bestKey || row.key > bestKey) bestKey = row.key;
  }
  if (!bestKey) return [];
  return keyed.filter((row) => row.key === bestKey).map((row) => row.item);
}

/** Latest scrape row on or before dateLabel (YYYY-MM-DD). If no date, latest row. */
export function pickLatestRowOnOrBeforeDate(items, dateLabel) {
  const dated = rowsOnLatestDateOnOrBefore(items, dateLabel);
  if (dated.length) return dated[0];
  if (dateLabel || !items?.length) return null;
  return items[0];
}

export function filterRowsForProductPopup(rows, title, country) {
  if (!title) return [];
  let items = (rows || []).filter((item) => item?.title === title);
  const slug = productDetailCountrySlug(country);
  if (slug) {
    items = items.filter((item) => countrySlugMatches(item.country, slug));
  }
  return items;
}

export function productTitleCountryKey(title, country) {
  return `${title || ''}\x1f${productDetailCountrySlug(country) || ''}`;
}

/** Shareable product-detail path: /{projectKey}/product-detail/{country}/{hash}[?date=YYYY-MM-DD]. */
export function buildProductDetailPath(projectKey, country, title, dateKey) {
  const key = projectKey || 'jti-global';
  if (!title) return `/${key}/product-detail`;
  const hash = hashProductId(title);
  const slug = productDetailCountrySlug(country);
  const path = slug
    ? `/${key}/product-detail/${slug}/${hash}`
    : `/${key}/product-detail/${hash}`;
  const sqlDate = toSqlDateKey(dateKey);
  if (!sqlDate) return path;
  return `${path}?date=${encodeURIComponent(sqlDate)}`;
}

/**
 * Resolve a single product image URL from raw API data (image, box_image_urls, image_urls, media_gallery_urls).
 * Handles arrays from IQOS (image_urls), GLO (box_image_urls), Italy/GLO (media_gallery_urls): skip URLs
 * containing "new-slider" (e.g. Italian IQOS color.png), otherwise use first URL.
 * @param {string|array|object} raw - Raw value from API
 * @returns {string|null} Resolved URL or null
 */
export function resolveProductImageUrl(raw) {
  if (!raw) return null;

  const normalizeSingle = (value) => {
    if (!value || typeof value !== 'string') return null;
    value = value.trim();
    if (!value || value.toLowerCase() === 'null' || value.toLowerCase() === 'none') return null;
    if (value.startsWith('//')) value = `https:${value}`;
    if (typeof window !== 'undefined' && window.location?.protocol === 'https:' && value.startsWith('http://')) {
      value = `https://${value.slice('http://'.length)}`;
    }
    // Scheme-less www. — browsers treat as relative path otherwise
    if (/^www\./i.test(value)) {
      value = `https://${value}`;
    }
    if (
      /^https?:\/\//i.test(value) ||
      value.startsWith('data:image') ||
      value.startsWith('/') ||
      value.startsWith('./') ||
      value.startsWith('../')
    ) {
      return value;
    }
    return null;
  };

  const extractUrls = (val) => {
    if (Array.isArray(val)) {
      return val.map((entry) => {
        const candidate = entry && typeof entry === 'object'
          ? (entry.url || entry.href || entry.src || entry.value)
          : entry;
        return typeof candidate === 'string' ? candidate.trim() : null;
      }).filter(Boolean);
    }
    if (typeof val === 'string') {
      if (val.startsWith('[')) {
        try {
          const parsed = JSON.parse(val.replace(/'/g, '"'));
          return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [val];
        } catch {
          return val.split(/\s*[,|;]\s*/).map((s) => s.trim()).filter(Boolean);
        }
      }
      return [val];
    }
    return [];
  };

  if (Array.isArray(raw)) {
    const urls = extractUrls(raw);
    const skipNewSlider = (url) => !url.includes('new-slider');
    const filtered = urls.filter(skipNewSlider);
    const toUse = filtered.length > 0 ? filtered[0] : urls[0];
    return toUse ? normalizeSingle(toUse) : null;
  }

  if (typeof raw === 'object') {
    const candidate = raw.url || raw.href || raw.src || raw.value;
    return resolveProductImageUrl(candidate);
  }

  const str = String(raw).trim();
  if (!str || str.toLowerCase() === 'null' || str.toLowerCase() === 'none') return null;
  if (str.startsWith('[')) {
    try {
      const parsed = JSON.parse(str.replace(/'/g, '"'));
      return resolveProductImageUrl(parsed);
    } catch {
      /* fall through */
    }
  }
  // Comma/pipe-separated URLs
  const splitCandidates = str.split(/\s*[,|;]\s*/).filter(Boolean);
  if (splitCandidates.length > 1) {
    const firstUrl =
      splitCandidates.find((c) => /^https?:\/\//i.test(c) || c.startsWith('data:image')) ||
      splitCandidates.find((c) => c.startsWith('//') || c.startsWith('/') || c.startsWith('./') || c.startsWith('../')) ||
      splitCandidates[0];
    const normalized = normalizeSingle(firstUrl);
    if (normalized) return normalized;
  }
  const single = normalizeSingle(str);
  if (single) return single;
  // Last resort: extract URL-like substrings from list/dict reprs that are not valid JSON,
  // e.g. GLO galleries stored as "['https://...' 'https://...']" (no commas) or "{'url': 'https://...'}".
  const urlMatches = str.match(/(?:https?:)?\/\/[^\s'"\\\]},]+|data:image[^\s'"\\\]},]+/gi);
  if (urlMatches && urlMatches.length) {
    const nonSlider = urlMatches.filter((u) => !u.includes('new-slider'));
    return normalizeSingle(nonSlider[0] || urlMatches[0]);
  }
  return null;
}

/**
 * Resolve a product image URL from multiple candidate fields, trying each in priority
 * order and returning the first that produces a valid URL.
 *
 * This is important for retailers like GLO whose usable image lives in box_image_urls /
 * media_gallery_urls while an earlier field (e.g. `image`) may contain a non-empty but
 * unresolvable value. A plain `a || b || c` chain would lock onto that first non-empty
 * field and never fall back, hiding the image.
 *
 * @param {...(string|array|object)} candidates - Raw values in priority order
 * @returns {string|null} First resolvable URL or null
 */
export function resolveProductImageUrlFromFields(...candidates) {
  for (const candidate of candidates) {
    if (candidate == null || candidate === '') continue;
    const resolved = resolveProductImageUrl(candidate);
    if (resolved) return resolved;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Product detail / BAT matching — same field priority as BatProductMatchingView
// ValidateProductCard (retailer: image → imageUrl → img; reference: Image, imageUrl).
// ---------------------------------------------------------------------------

/** Safely parse a value that might be JSON (e.g. double-encoded row), same idea as BatProductMatchingView.safeParse */
export function safeParseJsonLike(value) {
  if (value == null) return value;
  if (typeof value === 'object') return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (_) {
      return value;
    }
  }
  return value;
}

/**
 * Flat row or nested `{ product }` (BAT possible_match shape).
 * If the whole row is a JSON string, parse it once.
 */
export function coerceProductRowObject(productData) {
  if (productData == null) return null;
  let row = productData;
  if (typeof row === 'string') {
    const parsed = safeParseJsonLike(row);
    row = typeof parsed === 'object' && parsed !== null ? parsed : null;
  }
  if (!row || typeof row !== 'object') return null;
  const inner = row.product ?? row;
  return typeof inner === 'object' && inner !== null ? inner : null;
}

/**
 * Ordered keys: BAT validate first, then JTI/IQOS-style bulk fields used elsewhere.
 * (Previously `imageUrl` could lose to `box_image_urls` if that key appeared first.)
 */
const PRODUCT_IMAGE_FIELD_KEYS_ORDERED = [
  'image',
  'imageUrl',
  'img',
  'Image',
  'ImageUrl',
  'image_url',
  'box_image_urls',
  'image_urls',
  'media_gallery_urls',
  'ImageURL',
  'ImageUrl_ref',
  'image_url_ref',
  'IMAGE',
  'Image URL',
  'image url',
  'thumbnail_url',
  'thumbnail',
  'picture_url',
  'picture',
  'product_image',
  'photo_url',
  'photoUrl',
  'Photo URL',
  'Product Image',
];

/** Raw cell value for resolveProductImageUrl (arrays, JSON strings, pipe-separated). */
export function pickRawImageFromProductRow(product) {
  if (!product || typeof product !== 'object') return null;
  for (const k of PRODUCT_IMAGE_FIELD_KEYS_ORDERED) {
    if (Object.prototype.hasOwnProperty.call(product, k)) {
      const v = product[k];
      if (v != null && v !== '') return v;
    }
  }
  for (const [k, v] of Object.entries(product)) {
    if (v == null || v === '') continue;
    const lk = String(k).toLowerCase();
    if (
      (lk.includes('image') ||
        lk.includes('picture') ||
        lk.includes('photo') ||
        lk.includes('thumbnail')) &&
      (typeof v === 'string' || Array.isArray(v))
    ) {
      return v;
    }
  }
  return null;
}

/**
 * Extra normalization when resolveProductImageUrl does not return (edge-case strings).
 */
export function normalizeImageUrlLoose(raw) {
  if (!raw) return null;
  if (Array.isArray(raw)) {
    for (const entry of raw) {
      const candidate =
        entry && typeof entry === 'object'
          ? entry.url || entry.href || entry.src || entry.value
          : entry;
      const normalized = normalizeImageUrlLoose(candidate);
      if (normalized) return normalized;
    }
    return null;
  }
  let value = raw;
  if (typeof value !== 'string') {
    try {
      value = value.toString();
    } catch (e) {
      return null;
    }
  }
  value = value.trim();
  if (!value || value.toLowerCase() === 'null' || value.toLowerCase() === 'none') return null;

  if ((value.startsWith('[') && value.endsWith(']')) || (value.startsWith('"') && value.endsWith('"'))) {
    try {
      const parsed = JSON.parse(value);
      return normalizeImageUrlLoose(parsed);
    } catch (e) {
      // ignore
    }
  }

  const splitCandidates = value.split(/\s*\|\s*|\s*,\s*/g).filter(Boolean);
  if (splitCandidates.length > 1) {
    const firstUrl = splitCandidates.find(
      (candidate) => /^https?:\/\//i.test(candidate) || candidate.startsWith('data:image')
    );
    if (firstUrl) value = firstUrl;
  }

  if (value.startsWith('//')) {
    value = `https:${value}`;
  }

  if (typeof window !== 'undefined' && window.location?.protocol === 'https:' && value.startsWith('http://')) {
    value = `https://${value.slice('http://'.length)}`;
  }

  if (/^www\./i.test(value)) {
    value = `https://${value}`;
  }

  return value;
}

/**
 * Single URL for ProductImageBox / img src — matches BAT validate behaviour + shared pipelines.
 */
export function getResolvedProductDisplayImageUrl(productData) {
  const row = coerceProductRowObject(productData);
  if (!row) return null;
  const raw = pickRawImageFromProductRow(row);
  if (raw == null || raw === '') return null;
  return resolveProductImageUrl(raw) || normalizeImageUrlLoose(raw) || null;
}

/**
 * Effective brand for a SKU (persisted override wins over dataset `brand`).
 * @param {string} title
 * @param {string} [datasetBrand]
 * @param {Record<string, string>} [brandOverrides]
 */
export function getEffectiveBrandFromOverrides(title, datasetBrand, brandOverrides) {
  if (title && brandOverrides && Object.prototype.hasOwnProperty.call(brandOverrides, title)) {
    return brandOverrides[title];
  }
  const trimmed = title != null ? String(title).trim() : '';
  if (trimmed && brandOverrides && Object.prototype.hasOwnProperty.call(brandOverrides, trimmed)) {
    return brandOverrides[trimmed];
  }
  return datasetBrand || '';
}

/**
 * Expand override maps so lookups succeed with trimmed titles.
 * @param {Record<string, any>} [overrides]
 */
export function indexOverridesByTitle(overrides) {
  if (!overrides || typeof overrides !== 'object') return {};
  const out = { ...overrides };
  Object.keys(overrides).forEach((key) => {
    if (key == null || key === '') return;
    const trimmed = String(key).trim();
    if (trimmed && !Object.prototype.hasOwnProperty.call(out, trimmed)) {
      out[trimmed] = overrides[key];
    }
  });
  return out;
}

/**
 * Look up a persisted override by current title and optional original/pre-rename title.
 * @param {Record<string, any>} [overrides]
 * @param {...string} titles
 */
export function getOverrideValue(overrides, ...titles) {
  if (!overrides) return undefined;
  for (const title of titles) {
    if (title == null || title === '') continue;
    if (Object.prototype.hasOwnProperty.call(overrides, title) && overrides[title] !== undefined && overrides[title] !== '') {
      return overrides[title];
    }
    const trimmed = String(title).trim();
    if (
      trimmed &&
      trimmed !== title &&
      Object.prototype.hasOwnProperty.call(overrides, trimmed) &&
      overrides[trimmed] !== undefined &&
      overrides[trimmed] !== ''
    ) {
      return overrides[trimmed];
    }
  }
  return undefined;
}

/**
 * Apply persisted JTI category + brand overrides onto a data row (returns same ref if unchanged).
 */
export function mergeJtiProductRowOverrides(item, categoryOverrides = {}, brandOverrides = {}) {
  if (!item || typeof item !== 'object') return item;
  const title = item.title;
  if (!title) return item;
  const categoryOverride = getOverrideValue(categoryOverrides, title, item.original_title);
  const hasBrandOverride =
    Boolean(brandOverrides) &&
    (Object.prototype.hasOwnProperty.call(brandOverrides, title) ||
      Object.prototype.hasOwnProperty.call(brandOverrides, String(title).trim()));
  if (!categoryOverride && !hasBrandOverride) return item;
  const next = { ...item };
  if (categoryOverride) next.category_group = categoryOverride;
  if (hasBrandOverride) next.brand = getEffectiveBrandFromOverrides(title, item.brand, brandOverrides);
  return next;
}

/** API flag key — boolean; independent from starter kit / limited edition / promo. */
export const PRODUCT_FLAG_FOR_NEW_USERS_ONLY = 'isForNewUsersOnly';

export const PRODUCT_FLAG_LABEL_FOR_NEW_USERS_ONLY = 'New Users Only';

export const PRODUCT_FLAG_PRICE_BANNER = 'isPriceBannerProduct';
export const PRODUCT_FLAG_LABEL_PRICE_BANNER = 'Price banner product';

/** Who can see the SKU on the retailer site — not a promo flag. */
export const PRODUCT_FLAG_SKU_VISIBILITY = 'skuVisibility';

export const PRODUCT_FLAG_LABEL_SKU_VISIBILITY = 'Audience visibility';

export const SKU_VISIBILITY_EVERYONE = 'everyone';
export const SKU_VISIBILITY_REGISTERED = 'registered';
export const SKU_VISIBILITY_METADATA = 'metadata';
export const SKU_VISIBILITY_DEFAULT = SKU_VISIBILITY_EVERYONE;

export const SKU_VISIBILITY_OPTIONS = [
  { value: SKU_VISIBILITY_EVERYONE, label: 'For everyone' },
  { value: SKU_VISIBILITY_REGISTERED, label: 'For registered users' },
  { value: SKU_VISIBILITY_METADATA, label: 'Only in metadata' },
];

const SKU_VISIBILITY_VALUES = new Set(SKU_VISIBILITY_OPTIONS.map((opt) => opt.value));

export function normalizeSkuVisibility(value) {
  const raw = typeof value === 'string' ? value.trim().toLowerCase() : value;
  return SKU_VISIBILITY_VALUES.has(raw) ? raw : SKU_VISIBILITY_DEFAULT;
}

export function getSkuVisibilityLabel(value) {
  const normalized = normalizeSkuVisibility(value);
  return SKU_VISIBILITY_OPTIONS.find((opt) => opt.value === normalized)?.label || 'For everyone';
}

function getProductFlagsRecord(productFlags, title) {
  if (!productFlags || !title) return null;
  if (productFlags[title]) return productFlags[title];
  const trimmed = String(title).trim();
  if (trimmed && productFlags[trimmed]) return productFlags[trimmed];
  const lower = (trimmed || String(title)).toLowerCase();
  for (const key of Object.keys(productFlags)) {
    if (String(key).trim().toLowerCase() === lower) return productFlags[key];
  }
  return null;
}

export function getSkuVisibility(productFlags, title) {
  const flags = getProductFlagsRecord(productFlags, title);
  return normalizeSkuVisibility(flags?.[PRODUCT_FLAG_SKU_VISIBILITY]);
}

export function isProductForNewUsersOnly(productFlags, title) {
  if (!productFlags || !title) return false;
  if (productFlags[title]?.[PRODUCT_FLAG_FOR_NEW_USERS_ONLY]) return true;
  const trimmed = String(title).trim();
  if (trimmed && productFlags[trimmed]?.[PRODUCT_FLAG_FOR_NEW_USERS_ONLY]) return true;
  const lower = (trimmed || String(title)).toLowerCase();
  for (const key of Object.keys(productFlags)) {
    if (String(key).trim().toLowerCase() === lower) {
      return !!productFlags[key]?.[PRODUCT_FLAG_FOR_NEW_USERS_ONLY];
    }
  }
  return false;
}
